import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { ArticleMedia } from 'designsystem/components/2-molecules/Article';
import { NewsPage } from 'designsystem/components/5-templates/NewsPage';
import { ConvertToPictureObject } from 'designsystem/utilities/convertToPictureObject';
import { CludoLang } from 'features/Layouts/Layout/Vendors/Cludo/cludoHelpers';
import { AsideFeature } from 'features/Modules/Aside';
import { SingleContentSectionFeature } from 'features/Modules/ContentSection/SingleContentSectionFeature';
import { ModuleFeature } from 'features/Modules/DynamicModule';
import { ImageFeature } from 'features/Modules/Image/ImageFeature';
import { RelatedListFeature } from 'features/Modules/RelatedList';
import { RichTextFeature } from 'features/Modules/RichText';
import { convertForRichTextFeature } from 'features/Modules/RichText/richTextFeatureAdapter';
import { TopBannerFeature } from 'features/Modules/TopBanner';
import { getArticleGroupId } from 'helpers/getArticleGroupId';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

export const NewsPageFeature: React.FC<Content.NewsPage> = ({ properties, additionalTop, additionalPageContent }) => {
	const {
		tags,
		teaser,
		types,
		heading,
		hideRelatedPages = false,
		date,
		expirationDate,
		image: oldImage,
		picture,
		caption,
		text,
		asideContent,
		pageSection,
		pageSections,
		bannerContent,
	} = properties ?? {};
	const pictureObject = ConvertToPictureObject(picture);

	const siteContext = useSiteContext();
	const pageContext = usePageContext();
	const dictionary = useDictionaryContext();
	const router = useRouter();

	const { theme } = siteContext ?? {};

	const lang = router.locale.split('-')[0] as CludoLang;
	const { hideDates } = pageContext?.configuration ?? {};
	const dateWithoutTime = date.split('T')[0];
	const expirationDateWithoutTime = expirationDate?.split('T')[0];

	const url = pictureObject !== null ? pictureObject.url : oldImage?.[0]?.url;
	const focalPoint = pictureObject !== null ? pictureObject.focalPoint : oldImage?.[0]?.focalPoint;
	const altText = pictureObject !== null ? pictureObject.altText : oldImage?.[0]?.properties.altText;
	const altTextChecked = altText == '""' ? '' : altText;

	const groupId = getArticleGroupId({ theme, id: pageContext.parent?.id, locale: lang });

	const moduleContainerSettings = {
		properties: {
			moduleMarginBottom: pageContext.moduleMarginBottom,
		},
	};

	const [hasFullWidthLanguageBar, setHasFullWidthLanguageBar] = useState(false);
	const [hasFullWidthBanner, setHasFullWidthBanner] = useState(false);
	const [hasFullWidthHeader] = useState(false);

	useEffect(() => {
		const heroImage = bannerContent?.items?.[0] as unknown as Content.HeroImage;
		const heroImagefullwidthImage = heroImage?.content.properties.fullWidthImage;

		setHasFullWidthBanner(heroImagefullwidthImage);
		setHasFullWidthLanguageBar(heroImagefullwidthImage);
	}, [bannerContent?.items]);

	return (
		<>
			<Head>
				<meta name="ArticleTypes" content={types?.map((tag) => tag.name).join('|')} />
				<meta name="ArticleTags" content={tags?.map((tag) => tag.name).join('|')} />
				<meta name="ArticleExpirationDate" content={expirationDateWithoutTime ? expirationDateWithoutTime : null} />
				<meta name="ArticleGroupId" content={groupId} />
				<meta name="ArticleImage" content={url} />

				<meta name="ArticleDate" content={hideDates ? null : dateWithoutTime ? dateWithoutTime : null} />
				<meta property="article:published_time" content={dateWithoutTime} />
				<meta name="dc.date.created" content={dateWithoutTime} />
				<meta name="dcterms.created" content={dateWithoutTime} />
				<meta name="pubDate" content={dateWithoutTime.replaceAll('-', '')} />
			</Head>
			<NewsPage
				additionalTop={additionalTop}
				additionalTopSettings={{
					fullWidth: {
						hasFullWidthLanguageBar: hasFullWidthLanguageBar,
						hasFullWidthBanner: hasFullWidthBanner,
						hasFullWidthHeader: hasFullWidthHeader,
					},
				}}
				additionalPageContent={additionalPageContent}
				languageBar={additionalTop.languageBar}
				moduleContainerSettings={moduleContainerSettings}
				heading={heading}
				teaser={teaser}
				date={hideDates ? null : dateWithoutTime}
				image={
					url ? (
						<ArticleMedia
							caption={caption}
							image={
								url && (
									<ImageFeature
										url={url}
										altText={altTextChecked}
										focalPoint={focalPoint}
										sizes="(max-width: 1248px) 100vw, 1080px"
									/>
								)
							}
							width="MaxWidth"
							addBottomMargin
						/>
					) : null
				}
				richText={text && <RichTextFeature content={convertForRichTextFeature(text?.markup)} />}
				aside={
					<AsideFeature>
						{asideContent?.items?.map((pageSection, index) => (
							<ModuleFeature key={index} contentModule={pageSection} />
						))}
						{!hideRelatedPages && (
							<RelatedListFeature
								parentId={pageContext.parent?.id}
								tags={tags?.map((tag) => tag.name)}
								types={types?.map((type) => type.name)}
								heading={dictionary.getLabel('RelatedNews')}
							/>
						)}
					</AsideFeature>
				}
				topBanner={bannerContent?.items?.length > 0 && <TopBannerFeature bannerContent={bannerContent} />}
				expirationDate={new Date(expirationDateWithoutTime).getFullYear() === 1 ? undefined : expirationDateWithoutTime}
				expirationDateText={{
					validFrom: dictionary.getLabel('ValidFrom'),
					validTo: dictionary.getLabel('ValidTo'),
					historicLabel: dictionary.getServiceInfo('HistoricNotApplicable'),
				}}
			>
				<SingleContentSectionFeature pageSection={pageSection?.items} noBackground />
				{pageSections?.items?.map((pageSectionItems, index) => (
					<SingleContentSectionFeature
						key={index + 'PageSection'}
						pageSection={pageSectionItems.content.properties.content?.items}
						noBackground
					/>
				))}
			</NewsPage>
		</>
	);
};
